/**
 * Casework (design section 11, node kgcp3): the inbox's shapes (worker/src/casework.js answers them) and the pure
 * helpers the page and the tests share.
 */
export type CaseStatus = "open" | "waiting" | "closed";
export type CaseTab = "open" | "waiting" | "mine" | "closed" | "all";

export interface CaseRow {
  id: string; title: string; category: string | null; name: string | null; email: string | null; phone: string | null;
  block: string | null; srNumber: string | null; ownerId: string | null; status: CaseStatus; dueAt: string | null;
  createdAt: string; updatedAt: string; closedAt: string | null; ageDays: number;
}
export interface CaseEvent { id: number; kind: "created" | "email" | "filed" | "city" | "reply" | "note" | "closed" | "reopened"; text: string; by: string | null; at: string }
export interface ServiceRequest { srNumber: string; type: string | null; department: string | null; status: string | null; createdAt: string | null; closedAt: string | null; address: string | null; block: string | null }
export interface CasePattern { n: number; noun: string; since: string | null; block: string; requests: { srNumber: string; address: string | null; status: string | null; createdAt: string | null }[] }
export interface CaseDetail extends CaseRow { notes: string | null; events: CaseEvent[]; request: ServiceRequest | null; pattern: CasePattern | null }
export interface CaseStats { open: number; dueThisWeek: number; waiting: number; medianDaysToClose: number | null; previous: { days: number; month: string } | null; closedThisMonth: number }
export interface Member { id: string; name: string }
export interface CaseworkSnapshot { at: string; ward: number | null; me: string; members: Member[]; stats: CaseStats; cases: CaseRow[]; selected: CaseDetail | null }

/** The tabs' rows: Open (open + waiting), Waiting on the City, Assigned to me (not closed), Closed, All. */
export function casesFor(tab: CaseTab, cases: CaseRow[], me: string): CaseRow[] {
  if (tab === "open") return cases.filter((c) => c.status !== "closed");
  if (tab === "waiting") return cases.filter((c) => c.status === "waiting");
  if (tab === "mine") return cases.filter((c) => c.status !== "closed" && c.ownerId === me);
  if (tab === "closed") return cases.filter((c) => c.status === "closed");
  return cases;
}

/** How the Age cell reads: past its due date (or a week old without one) = late; due within two days = soon. */
export function ageTone(c: Pick<CaseRow, "status" | "dueAt" | "ageDays">, now = Date.now()): "late" | "soon" | null {
  if (c.status === "closed") return null;
  if (c.dueAt) {
    const left = Date.parse(c.dueAt) - now;
    return left < 0 ? "late" : left <= 2 * 86_400_000 ? "soon" : null;
  }
  return c.ageDays >= 7 ? "late" : null;
}

/** "Rosa Medina" → "RM". */
export const initialsOf = (name: string | null | undefined) =>
  String(name ?? "").split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).filter((_, i, a) => i === 0 || i === a.length - 1).join("") || "·";

export const firstName = (name: string | null | undefined) => String(name ?? "").trim().split(/\s+/)[0] ?? "";

const TIME = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
/** "Oct 6, 9:12 AM" (Chicago time). */
export const eventTime = (iso: string) => TIME.format(new Date(iso));

/** "4 more pothole cases on this block since July". */
export const patternLine = (p: CasePattern) => `${p.n} more ${p.noun} ${p.n === 1 ? "case" : "cases"} on this block${p.since ? ` since ${p.since}` : ""}`;

export const STATUS_LABEL: Record<CaseStatus, string> = { open: "Open", waiting: "Waiting on the City", closed: "Closed" };
